import type { ServerEvent } from '@keep/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { labelsKey } from '../api/labels';
import { meKey } from '../api/auth';
import { noteKeys } from '../api/notes-cache';
import { clientId } from '../lib/client-id';

/**
 * Écoute le flux temps réel de l'API et invalide les données concernées.
 * EventSource gère seul la reconnexion ; à chaque reconnexion on recharge tout,
 * car des événements ont pu être manqués pendant la coupure.
 */
export function useServerEvents(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    const source = new EventSource(`/api/v1/events?clientId=${encodeURIComponent(clientId)}`);
    let hadError = false;

    source.onopen = () => {
      if (hadError) void queryClient.invalidateQueries();
      hadError = false;
    };
    source.onerror = () => {
      hadError = true;
    };
    source.onmessage = (message) => {
      let event: ServerEvent;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      switch (event.type) {
        case 'notes':
          void queryClient.invalidateQueries({ queryKey: noteKeys.lists });
          for (const id of event.ids)
            void queryClient.invalidateQueries({ queryKey: noteKeys.detail(id) });
          break;
        case 'labels':
          void queryClient.invalidateQueries({ queryKey: labelsKey });
          break;
        case 'profile':
          void queryClient.invalidateQueries({ queryKey: meKey });
          break;
      }
    };
    return () => source.close();
  }, [enabled, queryClient]);
}
